import React, { useEffect, useState, useRef } from "react";
import Header from "../../../Componets/Header";
import toast from "react-hot-toast";
import moment from "moment";
import Loadercomponent from "../../../Componets/Loader";
import { Button, Select, DatePicker, Radio, Space } from "antd";
import { PrinterOutlined, DownloadOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { get_profit_loss_summary_report } from "../../../Controllers/Dashboard/Dashboard";
const { RangePicker } = DatePicker;
const { Option } = Select;

const ProfitLossSummary = () => {
  const [loader, setLoader] = useState(false);
  const [reports, setReports] = useState([]);
  const [totals, setTotals] = useState({});
  const [viewType, setViewType] = useState("monthly");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState("ALL");
  const [branches, setBranches] = useState([]);
  const [role, setRole] = useState("");

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const headers = {
    name: "Profit & Loss Summary",
    components: [
      {
        type: "search",
        name: "search",
        placeholder: "Search Period",
        value: "",
      }
    ],
    navigate: {
      tabs: [
        { name: "Reports", path: "/app/reports" },
        { name: "Profit & Loss Summary", path: "" }
      ]
    }
  };

  const fetchData = async () => {
    try {
      setLoader(true);

      const payload = {
        viewType,
        branch: selectedBranch,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (viewType === "monthly") {
        payload.year = selectedYear;
      } else if (viewType === "daily" && dateRange) {
        payload.startDate = dateRange[0].format("YYYY-MM-DD");
        payload.endDate = dateRange[1].format("YYYY-MM-DD");
      } else if (viewType === "daterange" && dateRange) {
        payload.startDate = dateRange[0].format("YYYY-MM-DD");
        payload.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await get_profit_loss_summary_report(payload);

      if (response.status) {
        setReports(response.data.reports || []);
        setTotals(response.data.totals || {});
        setRole(response.data.role);
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error(error.error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewType, selectedYear, dateRange, selectedBranch]);

  const downloadCSV = () => {
    try {
      if (!reports.length) {
        toast.error("No data available to download");
        return;
      }

      const headers = ["Period", "Net Sales", "COGS", "Expenses", "Net Profit"];
      const csvRows = [headers];

      reports.forEach((report) => {
        csvRows.push([
          report.period,
          report.net_sales.toFixed(3),
          report.cogs.toFixed(3),
          report.expenses.toFixed(3),
          report.net_profit.toFixed(3)
        ]);
      });

      csvRows.push([]);
      csvRows.push(["TOTAL", totals.net_sales, totals.cogs, totals.expenses, totals.net_profit]);

      const csvContent = csvRows.map(row => row.join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Profit_Loss_${viewType}_${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded successfully");
    } catch (error) {
      toast.error("Failed to download CSV");
    }
  };

  const downloadPDF = () => {
    try {
      if (!reports.length) {
        toast.error("No data available to download");
        return;
      }

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      
      doc.setFontSize(16);
      doc.text("Profit & Loss Summary Report", 14, 15);
      
      doc.setFontSize(10);
      let subtitle = `Report Type: ${viewType.toUpperCase()}`;
      if (viewType === "monthly") subtitle += ` - Year: ${selectedYear}`;
      if (dateRange && (viewType === "daily" || viewType === "daterange")) {
        subtitle += ` - ${dateRange[0].format("DD-MM-YYYY")} to ${dateRange[1].format("DD-MM-YYYY")}`;
      }
      doc.text(subtitle, 14, 25);
      doc.text(`Generated on: ${moment().format("DD-MM-YYYY HH:mm")}`, 14, 32);

      const tableData = reports.map(report => [
        report.period,
        report.net_sales.toFixed(3),
        report.cogs.toFixed(3),
        report.expenses.toFixed(3),
        report.net_profit.toFixed(3)
      ]);

      autoTable(doc, {
        startY: 40,
        head: [["Period", "Net Sales", "COGS", "Expenses", "Net Profit"]],
        body: tableData,
        foot: [["TOTAL", totals.net_sales, totals.cogs, totals.expenses, totals.net_profit]],
         styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [222, 222, 222],
          fillColor: false,
          lineWidth: 0.2
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [222, 222, 222],
          lineWidth: 0.2
        },
         alternateRowStyles: {
          fillColor: false,
        }
      });

      doc.save(`Profit_Loss_${viewType}_${moment().format("YYYY-MM-DD")}.pdf`);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const getProfitColor = (profit) => {
    if (profit > 0) return "text-green-600";
    if (profit < 0) return "text-red-600";
    return "text-gray-600";
  };

  return (
    <Header headers={headers} setHeaders={() => {}} formdata={() => {}}>
      <Loadercomponent loader={loader}>
        <div className="p-4">
          <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">View Type</label>
                <Radio.Group value={viewType} onChange={(e) => setViewType(e.target.value)}>
                  <Radio.Button value="daily">Daily</Radio.Button>
                  <Radio.Button value="monthly">Monthly</Radio.Button>
                  <Radio.Button value="yearly">Yearly</Radio.Button>
                  <Radio.Button value="daterange">Date Range</Radio.Button>
                </Radio.Group>
              </div>

              {viewType === "monthly" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <Select value={selectedYear} onChange={setSelectedYear} style={{ width: "100%" }}>
                    {years.map(year => <Option key={year} value={year}>{year}</Option>)}
                  </Select>
                </div>
              )}

              {(viewType === "daily" || viewType === "daterange") && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <RangePicker 
                    className="w-full"
                    onChange={(dates) => setDateRange(dates)}
                    format="DD-MM-YYYY"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={downloadCSV} icon={<DownloadOutlined />}>CSV</Button>
              <Button onClick={downloadPDF} icon={<PrinterOutlined />} type="primary">PDF</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="text-sm text-blue-600 font-medium">Total Net Sales</div>
              <div className="text-2xl font-bold text-blue-700">{parseFloat(totals.net_sales || 0).toLocaleString()}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <div className="text-sm text-orange-600 font-medium">Total COGS</div>
              <div className="text-2xl font-bold text-orange-700">{parseFloat(totals.cogs || 0).toLocaleString()}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="text-sm text-red-600 font-medium">Total Expenses</div>
              <div className="text-2xl font-bold text-red-700">{parseFloat(totals.expenses || 0).toLocaleString()}</div>
            </div>
            <div className={`rounded-lg p-4 border ${parseFloat(totals.net_profit || 0) >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
              <div className={`text-sm font-medium ${parseFloat(totals.net_profit || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>Net Profit</div>
              <div className={`text-2xl font-bold ${parseFloat(totals.net_profit || 0) >= 0 ? "text-green-700" : "text-red-700"}`}>
                {parseFloat(totals.net_profit || 0).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 border-b">Period</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 border-b">Net Sales</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 border-b">COGS</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 border-b">Expenses</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 border-b">Net Profit</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 border-b">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, index) => {
                    const margin = report.net_sales > 0 ? (report.net_profit / report.net_sales * 100) : 0;
                    return (
                      <tr key={index} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-2 text-sm text-gray-700">{report.period}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{report.net_sales.toFixed(3)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{report.cogs.toFixed(3)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{report.expenses.toFixed(3)}</td>
                        <td className={`px-4 py-2 text-sm text-right font-medium ${getProfitColor(report.net_profit)}`}>
                          {report.net_profit.toFixed(3)}
                        </td>
                        <td className={`px-4 py-2 text-sm text-center font-medium ${margin >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {margin.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                  {reports.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No data available for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
                {reports.length > 0 && (
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-gray-700 border-t">TOTAL</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-700 border-t">
                        {parseFloat(totals.net_sales || 0).toFixed(3)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-700 border-t">
                        {parseFloat(totals.cogs || 0).toFixed(3)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-700 border-t">
                        {parseFloat(totals.expenses || 0).toFixed(3)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-bold border-t ${getProfitColor(parseFloat(totals.net_profit || 0))}`}>
                        {parseFloat(totals.net_profit || 0).toFixed(3)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-center font-bold border-t ${parseFloat(totals.net_profit || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {totals.net_sales > 0 ? ((totals.net_profit / totals.net_sales) * 100).toFixed(2) : "0.00"}%
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </Loadercomponent>
    </Header>
  );
};

export default ProfitLossSummary;